import axios from "axios";
import { auth } from "../config/firebase";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

// Create axios instance with interceptors for Firebase auth
const apiClient = axios.create({
  baseURL: API_URL,
});

// Add request interceptor to include Firebase ID token to all requests
apiClient.interceptors.request.use(
  async (config) => {
    try {
      const user = auth.currentUser;
      if (user) {
        const idToken = await user.getIdToken();
        config.headers.Authorization = `Bearer ${idToken}`;
      } else {
        // Try to get token from localStorage (fallback)
        const storedToken = localStorage.getItem('firebaseIdToken');
        if (storedToken) {
          config.headers.Authorization = `Bearer ${storedToken}`;
        }
      }
    } catch (error) {
      console.warn('Failed to get Firebase token:', error);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle 401 responses (unauthorized) - redirect to login
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      // Clear auth data and redirect to login
      localStorage.removeItem('firebaseIdToken');
      localStorage.removeItem('firebaseUser');
      window.location.href = '/';
    }
    return Promise.reject(error);
  }
);

// Original API methods - export directly for backward compatibility
export const fetchSkillById = async (skillId, stage = 'development') => {
  const response = await apiClient.get(`/api/skill-info/${skillId}/${stage}`);
  return response.data;
};

export const createSkill = async (skillData) => {
  const response = await apiClient.post(`/api/create-skill`, skillData);
  return response.data;
};

export const fetchAllSkills = async () => {
  const response = await apiClient.get(`/api/skills`);
  return response.data.skills || response.data || [];
};

// New API methods for Alexa Skill publishing workflow
const authenticateWithAmazon = async () => {
  const response = await apiClient.post(`/api/amazon/authenticate`);
  return response.data;
};

const createAlexaSkill = async (accessToken, vendorId) => {
  const response = await apiClient.post(`/api/amazon/create-skill`, {
    accessToken,
    vendorId
  });
  return response.data;
};

const buildInteractionModel = async (accessToken, skillId, invocationName) => {
  const requestBody = {
    accessToken,
    skillId
  };
  
  // Include invocation name if provided (check for truthy and non-empty string)
  if (invocationName && typeof invocationName === 'string' && invocationName.trim().length > 0) {
    requestBody.invocationName = invocationName.trim();
    console.log(`📤 Sending invocation name to backend: "${requestBody.invocationName}"`);
  } else {
    console.log(`📤 No invocation name provided (value: ${invocationName}, type: ${typeof invocationName})`);
  }
  
  console.log(`📤 Request body being sent:`, JSON.stringify(requestBody, null, 2));
  
  const response = await apiClient.post(`/api/amazon/build-model`, requestBody);
  return response.data;
};

const enableTesting = async (accessToken, skillId) => {
  const response = await apiClient.post(`/api/amazon/enable-testing`, {
    accessToken,
    skillId
  });
  return response.data;
};

const submitForCertification = async (accessToken, skillId) => {
  const response = await apiClient.post(`/api/amazon/submit-certification`, {
    accessToken,
    skillId
  });
  return response.data;
};

const getSkillStatus = async (accessToken, skillId) => {
  const response = await apiClient.get(`/api/amazon/skill-status`, {
    params: { accessToken, skillId }
  });
  return response.data;
};

const deleteSkill = async (accessToken, skillId) => {
  const response = await apiClient.delete(`/api/amazon/skill/${skillId}`, {
    params: { accessToken }
  });
  return response.data;
};

const publishSkill = async (accessToken, skillId) => {
  const response = await apiClient.post(`/api/amazon/publish-skill`, {
    accessToken,
    skillId
  });
  return response.data;
};

const getPublicationStatus = async (skillId, language = 'en-US') => {
  const response = await apiClient.get(`/api/amazon/skill/${skillId}/publication-status`, {
    params: { language }
  });
  return response.data;
};

const createSubscription = async (skillId, subscriptionData) => {
  const response = await apiClient.post(`/api/skills/${skillId}/subscriptions`, {
    skillId,
    ...subscriptionData
  });
  return response.data;
};

const getSubscriptions = async (skillId) => {
  const response = await apiClient.get(`/api/skills/${skillId}/subscriptions`);
  return response.data;
};

const checkDuplicateInvocationName = async (invocationName, excludeSkillId = null) => {
  const params = { invocationName };
  if (excludeSkillId) {
    params.excludeSkillId = excludeSkillId;
  }
  const response = await apiClient.get(`/api/check-duplicate-invocation-name`, { params });
  return response.data;
};

// In-Skill Product Management Methods
const getInSkillProducts = async () => {
  const response = await apiClient.get(`/api/in-skill-products`);
  return response.data.products || [];
};

const getInSkillProduct = async (productId) => {
  const response = await apiClient.get(`/api/in-skill-products/${productId}`);
  return response.data;
};

const createInSkillProduct = async (productData) => {
  const response = await apiClient.post(`/api/in-skill-products`, productData);
  return response.data;
};

const updateInSkillProduct = async (productId, productData) => {
  const response = await apiClient.put(`/api/in-skill-products/${productId}`, productData);
  return response.data;
};

const deleteInSkillProduct = async (productId) => {
  const response = await apiClient.delete(`/api/in-skill-products/${productId}`);
  return response.data;
};

const getSkills = async () => {
  return fetchAllSkills();
};

// Export as a service object for new components
export const apiService = {
  fetchSkillById,
  createSkill,
  fetchAllSkills,
  getSkills,
  authenticateWithAmazon,
  createAlexaSkill,
  buildInteractionModel,
  enableTesting,
  submitForCertification,
  getSkillStatus,
  deleteSkill,
  publishSkill,
  getPublicationStatus,
  createSubscription,
  getSubscriptions,
  checkDuplicateInvocationName,
  // In-Skill Product methods
  getInSkillProducts,
  getInSkillProduct,
  createInSkillProduct,
  updateInSkillProduct,
  deleteInSkillProduct
};

// Export apiClient for direct use (e.g., file uploads)
export { apiClient };

// Default export for simpler imports
export default apiService;
